<template>
  <div class="flex justify-center">
    <div class="bulk-mail-modal bg-white modal-card">
      <header class="bulk-mail-modal__header">
        <div>
          <h2>Envoyer un mail</h2>
          <p>Rédigez une campagne immédiate ou planifiez son envoi.</p>
        </div>
        <button type="button" aria-label="Fermer" :disabled="loading" @click="$parent.close()">
          <i class="fas fa-times" />
        </button>
      </header>

      <div class="bulk-mail-modal__content p-6 space-y-6 overflow-y-auto">
      <section>
        <h3 class="text-sm font-semibold text-gray-600 uppercase tracking-wide">Modèle</h3>
        <div class="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3">
          <button
            v-for="option in templateOptions"
            :key="option.value"
            type="button"
            class="text-left border rounded-lg px-3 py-3 transition"
            :class="template === option.value ? 'border-primary-500 bg-primary-50 text-primary-700' : 'border-gray-200 hover:border-primary-300'"
            @click="template = option.value"
          >
            <p class="font-semibold">{{ option.label }}</p>
            <p class="text-xs text-gray-500 mt-1">{{ option.description }}</p>
          </button>
        </div>
      </section>

      <section>
        <h3 class="text-sm font-semibold text-gray-600 uppercase tracking-wide">Destinataires</h3>
        <div class="mt-3 flex flex-col sm:flex-row gap-3">
          <button
            v-for="option in scopeOptions"
            :key="option.value"
            type="button"
            class="flex-1 border rounded-lg px-3 py-2 transition"
            :class="scope === option.value ? 'border-primary-500 bg-primary-50 text-primary-700' : 'border-gray-200 hover:border-primary-300'"
            @click="scope = option.value"
          >
            <span class="text-sm">{{ option.label }}</span>
          </button>
        </div>
      </section>

      <section>
        <h3 class="text-sm font-semibold text-gray-600 uppercase tracking-wide">Date d'envoi</h3>
        <div class="mt-3 sm:max-w-xs">
          <t-datepicker
            v-model="scheduledAt"
            timepicker
            dateFormat="Y-m-d H:i:S"
            userFormat="j F, Y H:i"
            valueType="format"
            placeholder="Sélectionnez la date d'envoi…"
            clearable
            :close-on-select="false"
          />
          <p class="text-xs text-gray-500 mt-1">Laissez vide pour envoyer immédiatement.</p>
        </div>
      </section>

      <section>
        <h3 class="text-sm font-semibold text-gray-600 uppercase tracking-wide">Objet</h3>
        <input
          type="text"
          v-model="subject"
          class="mt-2 w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          placeholder="Objet de votre message"
        >
      </section>

      <section>
        <h3 class="text-sm font-semibold text-gray-600 uppercase tracking-wide">Titre de la campagne (Tracking)</h3>
        <input
          type="text"
          v-model="trackingTitle"
          class="mt-2 w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          placeholder="Facultatif (sinon objet du mail)"
        >
      </section>

      <section>
        <div class="flex items-center justify-between">
          <h3 class="text-sm font-semibold text-gray-600 uppercase tracking-wide">Message</h3>
          <button
            type="button"
            class="text-xs text-primary-600 hover:text-primary-700"
          @click="setDefaults()"
            v-if="template !== 'custom'"
          >
            Réinitialiser le texte par défaut
          </button>
        </div>
        <textarea
          v-model="body"
          rows="10"
          class="mt-2 w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          placeholder="Contenu du message (texte simple)"
          @paste="handleBodyPaste"
        ></textarea>
        <p class="text-xs text-gray-500 mt-1">
          Vous pouvez utiliser les variables suivantes :
          <code>{{ placeholderTokens.email }}</code>,
          <code>{{ placeholderTokens.domain }}</code>,
          <code>{{ placeholderTokens.application }}</code>,
          <code>{{ placeholderTokens.play_store }}</code>,
          <code>{{ placeholderTokens.app_store }}</code>.
          Elles seront remplacées automatiquement pour chaque destinataire.<br>
          Pour créer un lien vers un store, entourez votre texte avec
          <code v-pre>{{play_store}}Texte du lien{{/play_store}}</code>
          ou
          <code v-pre>{{app_store}}Texte du lien{{/app_store}}</code>.
        </p>
      </section>

      <section>
        <h3 class="text-sm font-semibold text-gray-600 uppercase tracking-wide">Aperçu</h3>
        <div class="mt-3 border border-gray-200 rounded-lg bg-gray-50 p-4">
          <p class="text-xs text-gray-500 uppercase tracking-wide">Objet</p>
          <p class="text-sm text-gray-800 font-medium mb-4">{{ previewSubject }}</p>
          <div
            class="text-sm text-gray-700 space-y-3 preview-email-content"
            v-html="previewBody"
          />
        </div>
      </section>
      </div>

      <footer class="bulk-mail-modal__footer">
        <button
          type="button"
          class="bulk-mail-modal__cancel"
          :disabled="loading"
          @click="$parent.close()"
        >
          Annuler
        </button>
        <button
          type="button"
          class="bulk-mail-modal__submit"
          :disabled="!canSubmit || loading"
          @click="submit"
        >
          <i :class="loading ? 'fas fa-spinner fa-spin' : 'fas fa-paper-plane'" />
          <span v-if="loading">{{ scheduledAt ? 'Programmation…' : 'Envoi…' }}</span>
          <span v-else>{{ scheduledAt ? 'Programmer l’envoi' : 'Envoyer maintenant' }}</span>
        </button>
      </footer>
    </div>
  </div>
</template>

<script>
import { parse, isValid } from 'date-fns'
import { http } from '@/services/api.service'

const PLACEHOLDER_CODES = {
  email: '{{email}}',
  domain: '{{domain}}',
  application: '{{application}}',
  playStore: '{{play_store}}',
  appStore: '{{app_store}}'
}

export default {
  name: 'UserBulkMailModal',
  data () {
    return {
      template: 'onboarding',
      scope: 'all',
      scheduledAt: '',
      subject: '',
      trackingTitle: '',
      body: '',
      loading: false,
      applicationUrls: {
        url_appstore: '',
        url_playstore: ''
      }
    }
  },
  computed: {
    application () {
      return this.$store.getters.application || {}
    },
    appInfo () {
      return this.application.app || {}
    },
    appLabel () {
      return this.appInfo.label || ''
    },
    appSlug () {
      return this.appInfo.slug || ''
    },
    domainLabel () {
      const domain = this.$store.getters.domain || {}
      return domain.label || ''
    },
    isTract () {
      return this.appSlug === 'tract'
    },
    templateOptions () {
      return [
        {
          value: 'onboarding',
          label: 'Onboarding téléchargement',
          description: 'Mail de bienvenue envoyé lors de la création d’un compte.'
        },
        {
          value: 'reminder',
          label: 'Relance téléchargements',
          description: 'Relance les utilisateurs n’ayant pas encore téléchargé l’application.'
        },
        {
          value: 'custom',
          label: 'Personnalisé',
          description: 'Rédiger un message sur mesure.'
        }
      ]
    },
    scopeOptions () {
      return [
        {
          value: 'all',
          label: 'Tous les utilisateurs du domaine'
        },
        {
          value: 'not_downloaded',
          label: 'Utilisateurs sans compte actif'
        }
      ]
    },
    previewSubject () {
      return this.replacePlaceholders(this.subject || 'Objet à définir', this.previewReplacements)
    },
    previewBody () {
      const filled = this.replacePlaceholders(this.body || '', this.previewReplacements)
      return this.formatBodyForPreview(filled)
    },
    canSubmit () {
      return this.subject.trim().length > 0 && this.body.trim().length > 0
    },
    defaultContent () {
      if (this.template === 'custom') {
        return { subject: '', body: '' }
      }
      return this.getTemplateDefaults()
    },
    isDefaultContent () {
      if (this.template === 'custom') {
        return false
      }
      const defaults = this.defaultContent
      return this.normalizeText(defaults.subject) === this.normalizeText(this.subject) &&
        this.normalizeText(defaults.body) === this.normalizeText(this.body)
    },
    placeholderTokens () {
      return {
        email: PLACEHOLDER_CODES.email,
        domain: PLACEHOLDER_CODES.domain,
        application: PLACEHOLDER_CODES.application,
        play_store: PLACEHOLDER_CODES.playStore,
        app_store: PLACEHOLDER_CODES.appStore
      }
    },
    previewReplacements () {
      return {
        [PLACEHOLDER_CODES.email]: 'prenom.nom@example.com',
        [PLACEHOLDER_CODES.domain]: this.domainLabel || 'Votre organisation',
        [PLACEHOLDER_CODES.application]: this.appLabel || 'Votre application',
        [PLACEHOLDER_CODES.playStore]: this.applicationUrls.url_playstore || 'https://play.google.com',
        [PLACEHOLDER_CODES.appStore]: this.applicationUrls.url_appstore || 'https://apps.apple.com'
      }
    }
  },
  watch: {
    template: {
      immediate: true,
      handler (newTemplate) {
        if (newTemplate === 'custom') {
          this.subject = ''
          this.body = ''
          return
        }
        this.setDefaults()
      }
    }
  },
  created () {
    this.fetchApplicationUrls()
  },
  methods: {
    fetchApplicationUrls () {
      const slug = process.env.VUE_APP_APPLICATION
      http.get(`/application/${slug}/urls`)
        .then(({ data }) => {
          this.applicationUrls = data || this.applicationUrls
        })
        .catch(() => {
          // silent fail, defaults already set
        })
    },
    setDefaults () {
      const defaults = this.getTemplateDefaults()
      this.subject = defaults.subject
      this.body = defaults.body
    },
    getTemplateDefaults () {
      if (this.template === 'reminder') {
        return {
          subject: this.getReminderSubject(),
          body: this.getReminderBody()
        }
      }
      return {
        subject: this.getOnboardingSubject(),
        body: this.getOnboardingBody()
      }
    },
    getOnboardingSubject () {
      const domainSegment = this.domainLabel ? this.domainLabel : 'votre organisation'
      return `Bienvenue sur ${this.appLabel} - l’outil qui vous connecte à vos élus ${domainSegment} !`
    },
    getReminderSubject () {
      const domainSegment = this.domainLabel ? this.domainLabel : 'votre organisation'
      return `Ne passez pas à côté de ${this.appLabel} - l’outil qui vous connecte à vos élus ${domainSegment} !`
    },
    getOnboardingBody () {
      const invitedBy = this.isTract
        ? `votre organisation syndicale ${this.domainLabel}`
        : `votre CSE ${this.domainLabel}`
      const invitationTitle = this.isTract ? `c'est quoi ${this.appLabel} ?` : `qu'est-ce que ${this.appLabel} ?`
      const invitationText = this.isTract
        ? `Grâce à ${this.appLabel}, nous avons à cœur de vous partager toutes les infos sur la vie de votre organisation syndicale et de répondre à vos interrogations.`
        : `C'est votre nouvel outil pour suivre l'actualité de la vie de votre entreprise, vous informer sur vos droits et donner votre avis sur les décisions de l'entreprise.`

      const downloadSteps = [
        `- Téléchargez l’application via :`,
        '  * {{play_store}}Google Play Store{{/play_store}}',
        '  * {{app_store}}Apple Store{{/app_store}}',
        `- Connectez-vous avec votre identifiant : ${PLACEHOLDER_CODES.email} et créez un mot de passe sécurisé.`,
        `- Validez votre inscription via le lien du mail de confirmation reçu.`
      ].join('\n')

      return [
        `Bonjour et bienvenue dans l’application ${this.appLabel} 🎉`,
        `Vous avez été invité(e) par ${invitedBy} à rejoindre ${this.appLabel}.`,
        `1) ${invitationTitle}`,
        invitationText,
        `2) Comment télécharger ${this.appLabel} ?`,
        downloadSteps,
        `Bienvenue à bord !`,
        `L’équipe ${this.appLabel}`
      ].join('\n\n')
    },
    getReminderBody () {
      const organisation = this.isTract ? 'organisation syndicale' : 'CSE'
      const benefits = this.isTract
        ? [
          'suivre toute l’actualité de votre organisation syndicale',
          'vous informer sur vos droits',
          'échanger facilement avec vos représentants syndicaux'
        ]
        : [
          'suivre l’actualité de la vie de votre entreprise',
          'vous informer sur vos droits',
          'donner votre avis sur les décisions de l’entreprise'
        ]

      const benefitsText = benefits.map(item => `- ${item}`).join('\n')
      const downloadSteps = [
        `- Téléchargez l’application via :`,
        '  * {{play_store}}Google Play Store{{/play_store}}',
        '  * {{app_store}}Apple Store{{/app_store}}',
        `- Connectez-vous avec votre identifiant : ${PLACEHOLDER_CODES.email} et créez un mot de passe sécurisé.`,
        '- Validez votre inscription via le lien du mail de confirmation reçu.'
      ].join('\n')

      return [
        'Bonjour,',
        `Vous n’avez pas encore téléchargé ${this.appLabel}, l’application mise à disposition par votre ${organisation} ${this.domainLabel} ? 😮`,
        `Pour rappel, ${this.appLabel} c’est :`,
        benefitsText,
        `Comment télécharger ${this.appLabel} ?`,
        downloadSteps,
        'Bienvenue à bord !',
        `L’équipe ${this.appLabel}`
      ].join('\n\n')
    },
    replacePlaceholders (text, replacements) {
      let result = text
      Object.entries(replacements).forEach(([placeholder, value]) => {
        const key = placeholder
        const safeValue = value || ''
        result = result.split(key).join(safeValue)
      })
      return result
    },
    normalizeText (value) {
      return (value || '')
        .replace(/\s+/g, ' ')
        .trim()
    },
    handleBodyPaste (event) {
      const clipboardData = event.clipboardData || window.clipboardData
      const text = clipboardData ? clipboardData.getData('text/plain') : ''

      if (!text) {
        return
      }

      event.preventDefault()

      const target = event.target
      const currentValue = this.body || ''
      const selectionStart = typeof target.selectionStart === 'number' ? target.selectionStart : currentValue.length
      const selectionEnd = typeof target.selectionEnd === 'number' ? target.selectionEnd : selectionStart

      this.body = `${currentValue.slice(0, selectionStart)}${text}${currentValue.slice(selectionEnd)}`

      this.$nextTick(() => {
        const nextCursorPosition = selectionStart + text.length
        if (typeof target.setSelectionRange === 'function') {
          target.setSelectionRange(nextCursorPosition, nextCursorPosition)
        }
      })
    },
    formatBodyForPreview (text) {
      const blocks = text.split(/\n{2,}/).map(block => block.trim()).filter(Boolean)
      const html = blocks.map(block => {
        const lines = block.split('\n')
        if (this.isUnorderedList(lines)) {
          const items = lines.map(line => {
            const clean = line.replace(/^\s*[-*]\s*/, '')
            return this.renderLineWithLinks(clean.trim())
          })
          return `<ul>${items.map(item => `<li>${item}</li>`).join('')}</ul>`
        }
        if (this.isOrderedList(lines)) {
          const items = lines.map(line => {
            const clean = line.replace(/^\s*\d+\.?\s*/, '')
            return this.renderLineWithLinks(clean.trim())
          })
          return `<ol>${items.map(item => `<li>${item}</li>`).join('')}</ol>`
        }
        const renderedLines = lines.map(line => this.renderLineWithLinks(line))
        return `<p>${renderedLines.join('<br>')}</p>`
      })
      return html.join('')
    },
    isUnorderedList (lines) {
      if (!lines.length) return false
      return lines.every(line => /^\s*[-*]\s+/.test(line) || /^\s*\*\s+/.test(line) || /^\s*[-*]\s*$/.test(line))
    },
    isOrderedList (lines) {
      if (!lines.length) return false
      return lines.every(line => /^\s*\d+\.?\s+/.test(line))
    },
    escapeHtml (value) {
      return value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;')
    },
    renderLineWithLinks (line) {
      const anchors = []
      const placeholder = () => `[[LINK${anchors.length}]]`

      const playStoreUrl = (this.applicationUrls && this.applicationUrls.url_playstore) || ''
      const appStoreUrl = (this.applicationUrls && this.applicationUrls.url_appstore) || ''

      let processed = line

      const registerAnchor = (label, url) => {
        if (!url) {
          return label
        }
        const token = placeholder()
        anchors.push({ token, html: `<a href="${this.escapeHtml(url)}" target="_blank" rel="noopener noreferrer">${this.escapeHtml(label)}</a>` })
        return token
      }

      processed = processed.replace(/\{\{play_store\}\}(.*?)\{\{\/play_store\}\}/gi, (_, label) => {
        const text = (label || '').trim() || 'Google Play Store'
        return registerAnchor(text, playStoreUrl)
      })

      processed = processed.replace(/\{\{app_store\}\}(.*?)\{\{\/app_store\}\}/gi, (_, label) => {
        const text = (label || '').trim() || 'Apple Store'
        return registerAnchor(text, appStoreUrl)
      })

      processed = processed.replace(/(Google Play Store)\s*:?-?\s*(https?:\/\/\S+)/gi, (_, label, url) => {
        return registerAnchor(label, url)
      })

      processed = processed.replace(/(Apple Store|App Store)\s*:?-?\s*(https?:\/\/\S+)/gi, (_, __, url) => {
        return registerAnchor('Apple Store', url)
      })

      processed = processed.replace(/https?:\/\/\S+/gi, (url) => {
        const token = placeholder()
        anchors.push({ token, html: `<a href="${this.escapeHtml(url)}" target="_blank" rel="noopener noreferrer">${this.escapeHtml(url)}</a>` })
        return token
      })

      let escaped = this.escapeHtml(processed)
      anchors.forEach(({ token, html }) => {
        escaped = escaped.replace(token, html)
      })
      return escaped
    },
    normalizeScheduledAt () {
      if (!this.scheduledAt) {
        return null
      }

      if (this.scheduledAt instanceof Date) {
        return Number.isNaN(this.scheduledAt.getTime()) ? null : this.scheduledAt.toISOString()
      }

      if (typeof this.scheduledAt === 'string') {
        const trimmed = this.scheduledAt.trim()
        if (!trimmed) return null

        const parsed = parse(trimmed, 'yyyy-MM-dd HH:mm:ss', new Date())
        if (isValid(parsed)) {
          return parsed.toISOString()
        }
      }

      return null
    },
    submit () {
      if (!this.canSubmit || this.loading) {
        return
      }
      this.loading = true

      const payload = {
        template: this.template,
        scope: this.scope,
        subject: this.subject.trim(),
        body: this.body.trim(),
        use_default: this.template !== 'custom' && this.isDefaultContent
      }

      const trackingTitle = this.trackingTitle.trim()
      if (trackingTitle) {
        payload.tracking_title = trackingTitle
      }

      const normalized = this.normalizeScheduledAt()
      if (normalized) {
        payload.scheduled_at = normalized
      }

      http.post('/user/mail/bulk', payload)
        .then(({ data }) => {
          const message = data?.message || 'Envoi programmé.'
          this.$notify({
            group: 'maviepro-success',
            text: message
          })
          this.$emit('sent', data)
          this.$parent.close()
        })
        .catch(err => {
          console.error(err)
          const message = err?.response?.data?.message || 'Erreur lors de la programmation de l’envoi.'
          this.$notify({
            group: 'maviepro-error',
            text: message
          })
        })
        .finally(() => {
          this.loading = false
        })
    }
  }
}
</script>

<style scoped>
.bulk-mail-modal {
  width: 90vw;
  max-width: 1200px;
  overflow: hidden;
  border-radius: 1rem;
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.2);
}

.bulk-mail-modal__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #e5e7eb;
  background: #fff;
}

.bulk-mail-modal__header h2 {
  color: #111827;
  font-size: 1.25rem;
  font-weight: 700;
}

.bulk-mail-modal__header p {
  margin-top: 0.25rem;
  color: #64748b;
  font-size: 0.875rem;
}

.bulk-mail-modal__header > button {
  display: inline-flex;
  width: 2rem;
  height: 2rem;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  border: 1px solid #d1d5db;
  border-radius: 9999px;
  color: #64748b;
}

.bulk-mail-modal__header > button:hover:not(:disabled) {
  background: #f8fafc;
  color: #111827;
}

.bulk-mail-modal__content {
  max-height: 70vh;
}

.bulk-mail-modal__footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1rem 1.5rem;
  border-top: 1px solid #e5e7eb;
  background: #f8fafc;
}

.bulk-mail-modal__cancel,
.bulk-mail-modal__submit {
  display: inline-flex;
  min-height: 42px;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.65rem 1rem;
  border-radius: 10px;
  font-size: 0.875rem;
  font-weight: 700;
}

.bulk-mail-modal__cancel {
  border: 1px solid #d1d5db;
  background: #fff;
  color: #475569;
}

.bulk-mail-modal__cancel:hover:not(:disabled) {
  background: #f1f5f9;
}

.bulk-mail-modal__submit {
  background: #2563eb;
  color: #fff;
}

.bulk-mail-modal__submit:hover:not(:disabled) {
  background: #1d4ed8;
}

.bulk-mail-modal__header > button:disabled,
.bulk-mail-modal__cancel:disabled,
.bulk-mail-modal__submit:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.bulk-mail-modal__header > button:focus-visible,
.bulk-mail-modal__cancel:focus-visible,
.bulk-mail-modal__submit:focus-visible {
  outline: 2px solid #2563eb;
  outline-offset: 2px;
}

.preview-email-content ul {
  list-style: disc;
  padding-left: 1.5rem;
  margin-bottom: 0.75rem;
}

.preview-email-content ol {
  list-style: decimal;
  padding-left: 1.5rem;
  margin-bottom: 0.75rem;
}

.preview-email-content p {
  margin-bottom: 0.75rem;
}

code {
  background: #edf2f7;
  padding: 0.1rem 0.35rem;
  border-radius: 0.25rem;
}
</style>
